/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * useGlobalValidationInterceptor
 * ─────────────────────────────────────────────────────────────────────────────
 * Replaces the browser's native OS-level constraint validation tooltip with a
 * themed inline error message that respects the CoreX design system.
 *
 * How it works:
 *  • Listens for `invalid` events on `document` in CAPTURE phase (the event
 *    does not bubble, so capture is the only way to intercept it globally).
 *  • Calls `e.preventDefault()` to suppress the native tooltip.
 *  • Injects a `.cx-field-error` <span> immediately after the invalid input,
 *    displaying the browser's already-localized `validationMessage`.
 *  • Removes the error span the moment the user edits the field.
 *
 * Mount once at app root — no per-form or per-input changes needed.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect } from 'react';

const ATTR = 'data-cx-err';

/**
 * Build the SVG warning icon as a safe DOM element — no innerHTML, no XSS risk.
 * All attributes are set via setAttribute / DOM APIs.
 */
function createWarningIcon(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';

  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('width', '12');
  svg.setAttribute('height', '12');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2.5');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS(ns, 'path');
  path.setAttribute('d', 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z');
  svg.appendChild(path);

  const line = document.createElementNS(ns, 'line');
  line.setAttribute('x1', '12'); line.setAttribute('y1', '9');
  line.setAttribute('x2', '12'); line.setAttribute('y2', '13');
  svg.appendChild(line);

  const dot = document.createElementNS(ns, 'circle');
  dot.setAttribute('cx', '12'); dot.setAttribute('cy', '17'); dot.setAttribute('r', '0.5');
  dot.setAttribute('fill', 'currentColor'); dot.setAttribute('stroke', 'none');
  svg.appendChild(dot);

  return svg;
}

/** Inject or reuse the error span that lives right after `input`. */
function getOrCreateError(input: Element): HTMLSpanElement {
  const existingId = input.getAttribute(ATTR);
  if (existingId) {
    const existing = document.getElementById(existingId);
    if (existing) return existing as HTMLSpanElement;
  }

  const id = `cx-err-${Math.random().toString(36).slice(2, 9)}`;
  const span = document.createElement('span');
  span.id = id;
  span.className = 'cx-field-error';
  span.setAttribute('role', 'alert');
  span.setAttribute('aria-live', 'polite');
  input.setAttribute(ATTR, id);
  input.insertAdjacentElement('afterend', span);
  return span;
}

/** Clear the error span and clean up the attribute. */
function clearError(input: Element): void {
  const id = input.getAttribute(ATTR);
  if (!id) return;
  document.getElementById(id)?.remove();
  input.removeAttribute(ATTR);
}

/** Populate the span with icon + message using safe DOM methods only. */
function renderError(span: HTMLSpanElement, message: string): void {
  // Clear previous content safely
  while (span.firstChild) span.removeChild(span.firstChild);

  span.appendChild(createWarningIcon());

  const text = document.createElement('span');
  text.textContent = message; // textContent — XSS-safe
  span.appendChild(text);
}

export function useGlobalValidationInterceptor(): void {
  useEffect(() => {
    function onInvalid(e: Event): void {
      const input = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (!input || !('validationMessage' in input)) return;

      // Suppress the browser's native OS tooltip
      e.preventDefault();

      // Mark the input so CSS can add the error ring
      input.setAttribute('data-cx-invalid', 'true');

      // Build / update the error span
      const span = getOrCreateError(input);
      renderError(span, input.validationMessage);

      // Clear on next user edit — whichever fires first
      const cleanup = (): void => {
        clearError(input);
        input.removeAttribute('data-cx-invalid');
      };
      input.addEventListener('input', cleanup, { once: true });
      input.addEventListener('change', cleanup, { once: true });
    }

    // Capture phase — `invalid` does not bubble
    document.addEventListener('invalid', onInvalid, true);
    return () => document.removeEventListener('invalid', onInvalid, true);
  }, []);
}
